import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { DataSource, QueryRequest, QueryResponse } from '../types/grafana';

/**
 * API Reference para o serviço Grafana
 */
export const grafanaApiRef = createApiRef<GrafanaApi>({
  id: 'plugin.grafana-observability.api',
});

/**
 * Interface para a API do Grafana
 */
export interface GrafanaApi {
  /**
   * Busca todas as fontes de dados disponíveis
   */
  getDataSources(): Promise<DataSource[]>;

  /**
   * Busca uma fonte de dados específica pelo UID
   */
  getDataSource(uid: string): Promise<DataSource>;

  /**
   * Executa uma query no Grafana
   */
  query(request: QueryRequest): Promise<QueryResponse>;
}

/**
 * Implementação da API do Grafana
 */
export class GrafanaApiClient implements GrafanaApi {
  private baseUrl: string = '';

  constructor(options: { discoveryApi: DiscoveryApi, fetchApi: FetchApi }) {
    const { discoveryApi, fetchApi } = options;
    
    // Inicializar a baseUrl
    discoveryApi.getBaseUrl('grafana-observability').then(url => {
      this.baseUrl = url;
    });
    
    // Usar o fetchApi para fazer requisições autenticadas
    this.fetchApi = fetchApi;
  }

  private readonly fetchApi: FetchApi;

  /**
   * Busca todas as fontes de dados disponíveis
   */
  async getDataSources(): Promise<DataSource[]> {
    const url = `${this.baseUrl}/datasources`;
    
    try {
      const response = await this.fetchApi.fetch(url);
      
      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to fetch data sources: ${response.status} ${error}`);
      }
      
      const data = await response.json();
      return data.datasources;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching data sources:', error);
      throw error;
    }
  }

  /**
   * Busca uma fonte de dados específica pelo UID
   */
  async getDataSource(uid: string): Promise<DataSource> {
    const url = `${this.baseUrl}/datasource/${uid}`;
    
    try {
      const response = await this.fetchApi.fetch(url);
      
      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to fetch data source: ${response.status} ${error}`);
      }
      
      const data = await response.json();
      return data.datasource;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error fetching data source ${uid}:`, error);
      throw error;
    }
  }

  /**
   * Executa uma query no Grafana
   */
  async query(request: QueryRequest): Promise<QueryResponse> {
    const url = `${this.baseUrl}/query`;
    
    try {
      const response = await this.fetchApi.fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });
      
      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Query failed: ${response.status} ${error}`);
      }
      
      return await response.json();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error executing query:', error);
      throw error;
    }
  }
}